import { useCallback, useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  exportSuperAdminComplaintThemesReport,
  getSuperAdminComplaintThemesFilters,
  postSuperAdminComplaintThemesReport,
} from "../api/superadminReports";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const TOOLTIP_TEXT = {
  scope: "Run this report across all salons, one business, or one salon.",
  business:
    "Required in Business scope. Optional in All Salons scope for narrowing.",
  salon:
    "Required in Salon scope. You can also narrow by salon after choosing a business.",
  include_cols:
    "Show Business and Salon columns in broader scopes to avoid merging similar labels across locations.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "complaint-themes",
  title: "Complaint Themes (Super Admin)",
  subtitle:
    "Review top complaint themes by staff, channel, and trend across scope.",
  summaryLabel: "Complaint Summary",
  detailsLabel: "Complaint Details",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("complaint-themes");

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fallback
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

export default function SuperAdminComplaintThemes() {
  const navigate = useNavigate();
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [includeBusinessSalonCols, setIncludeBusinessSalonCols] = useState(true);

  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((item) => {
      if (item?.id == null) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((item) => {
      if (item?.id == null) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [salons]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const salon = salonMap.get(String(salonId));
      if (salon?.timezone) return salon.timezone;
    }
    if (businessId) {
      const business = businessMap.get(String(businessId));
      if (business?.timezone) return business.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  const today = useMemo(
    () => formatDateISO(new Date(), anchorTimezone),
    [anchorTimezone],
  );
  const defaultStart = useMemo(
    () => formatDateISO(addDays(new Date(), -29), anchorTimezone),
    [anchorTimezone],
  );

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [bucket, setBucket] = useState("week");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const broadScope = scope === "all" || scope === "business";
  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (item) => String(item.business_id || "") === String(businessId),
    );
  }, [salons, businessId]);

  useEffect(() => {
    setStartDate(defaultStart);
    setEndDate(today);
  }, [defaultStart, today]);

  useEffect(() => {
    setIncludeBusinessSalonCols(broadScope);
  }, [broadScope]);

  useEffect(() => {
    if (!salonId) return;
    if (!filteredSalons.some((item) => String(item.id) === String(salonId))) {
      setSalonId("");
    }
  }, [salonId, filteredSalons]);

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    setFiltersError("");
    try {
      const data = await getSuperAdminComplaintThemesFilters();
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
    } catch (err) {
      setFiltersError(err?.message || "Unable to load filters.");
      setBusinesses([]);
      setSalons([]);
    } finally {
      setFiltersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!is_super_admin) return;
    loadFilters();
  }, [is_super_admin, loadFilters]);

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setSalonId("");
      return;
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
      return;
    }
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (
        selectedSalon &&
        value &&
        String(selectedSalon.business_id || "") !== String(value)
      ) {
        setSalonId("");
      }
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const loadReport = useCallback(async () => {
    if (scope === "business" && !businessId) {
      setError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setError("Choose a salon for Salon scope.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    if (startDate > endDate) {
      setError("End date can't be before start date.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await postSuperAdminComplaintThemesReport({
        scope,
        business_id: businessId ? Number(businessId) : undefined,
        salon_id: salonId ? Number(salonId) : undefined,
        start_date: startDate,
        end_date: endDate,
        bucket,
        include_business_salon_cols: broadScope
          ? Boolean(includeBusinessSalonCols)
          : false,
      });
      setReport(data || null);
    } catch (err) {
      setError(err?.message || "Unable to load report data.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [
    scope,
    businessId,
    salonId,
    startDate,
    endDate,
    bucket,
    broadScope,
    includeBusinessSalonCols,
  ]);

  useEffect(() => {
    if (!is_super_admin) return;
    loadReport();
  }, [is_super_admin, loadReport]);

  const handleExport = async () => {
    try {
      await exportSuperAdminComplaintThemesReport({
        scope,
        business_id: businessId ? Number(businessId) : undefined,
        salon_id: salonId ? Number(salonId) : undefined,
        start_date: startDate,
        end_date: endDate,
        bucket,
        include_business_salon_cols: broadScope
          ? Boolean(includeBusinessSalonCols)
          : false,
      });
    } catch (err) {
      setError(err?.message || "Unable to export CSV.");
    }
  };

  const topThemes = Array.isArray(report?.top_themes) ? report.top_themes : [];
  const byCategory = Array.isArray(report?.by_category) ? report.by_category : [];
  const byStaff = Array.isArray(report?.by_staff) ? report.by_staff : [];
  const byChannel = Array.isArray(report?.by_channel) ? report.by_channel : [];
  const byDay = Array.isArray(report?.by_day_of_week) ? report.by_day_of_week : [];
  const trend = Array.isArray(report?.trend) ? report.trend : [];
  const renderTable = (rows, label, showEntityColumns = false) => {
    const colCount = showEntityColumns ? 5 : 3;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left w-40">Theme</th>
              <th className="px-3 py-2 text-left">{label}</th>
              {showEntityColumns ? (
                <>
                  <th className="px-3 py-2 text-left">Business</th>
                  <th className="px-3 py-2 text-left">Salon</th>
                </>
              ) : null}
              <th className="px-3 py-2 text-right w-24">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-slate-500" colSpan={colCount}>
                  No data available.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => (
              <tr key={`${row.theme}-${row.key}-${idx}`} className="hover:bg-slate-50">
                <td className="px-3 py-3 text-slate-700">{row.theme}</td>
                <td className="px-3 py-3 text-slate-700">{row.label}</td>
                {showEntityColumns ? (
                  <>
                    <td className="px-3 py-3 text-slate-700">
                      {row.business_name || "--"}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {row.salon_name || "--"}
                    </td>
                  </>
                ) : null}
                <td className="px-3 py-3 text-right text-slate-700">
                  {row.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const timezoneNote =
    scope === "salon"
      ? "All dates use the salon timezone."
      : "All dates use each salon's local timezone.";

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin/reports", { replace: true });
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Complaint Themes
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6 text-sm md:text-base">
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Filters
                  </div>
                  <div className="text-sm text-slate-500">{timezoneNote}</div>
                </div>
                <div
                  id={HELP_ANCHORS.export}
                  className={`flex flex-wrap gap-2 rounded-2xl ${getSectionClassName(
                    HELP_ANCHORS.export
                  )}`}
                >
                  <button
                    type="button"
                    onClick={loadReport}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    Run report
                  </button>
                  <button
                    type="button"
                    onClick={handleExport}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <div
                id={HELP_ANCHORS.kpis}
                className={`grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 ${getSectionClassName(
                  HELP_ANCHORS.kpis
                )}`}
              >
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500 inline-flex items-center gap-2">
                    Scope
                    <InfoTooltip text={TOOLTIP_TEXT.scope} label="Scope" />
                  </label>
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    disabled={filtersLoading}
                  >
                    <option value="all">All Salons</option>
                    <option value="business">Business</option>
                    <option value="salon">Salon</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500 inline-flex items-center gap-2">
                    Business
                    <InfoTooltip text={TOOLTIP_TEXT.business} label="Business" />
                  </label>
                  <select
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    disabled={filtersLoading || !(scope === "all" || scope === "business")}
                  >
                    <option value="">
                      {scope === "business" ? "Select Business" : "All Businesses"}
                    </option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code ? `${business.code} - ` : ""}
                        {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500 inline-flex items-center gap-2">
                    Salon
                    <InfoTooltip text={TOOLTIP_TEXT.salon} label="Salon" />
                  </label>
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    disabled={
                      filtersLoading ||
                      !(
                        scope === "salon" ||
                        scope === "business" ||
                        Boolean(businessId)
                      )
                    }
                  >
                    <option value="">
                      {scope === "salon" ? "Select Salon" : "All Salons"}
                    </option>
                    {filteredSalons.map((salonItem) => (
                      <option key={salonItem.id} value={salonItem.id}>
                        {salonItem.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  {broadScope ? (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={includeBusinessSalonCols}
                        onChange={(event) =>
                          setIncludeBusinessSalonCols(event.target.checked)
                        }
                      />
                      <span>Show Business/Salon columns</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.include_cols}
                        label="Show Business/Salon columns"
                      />
                    </label>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Start date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    End date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Bucket
                  </label>
                  <select
                    value={bucket}
                    onChange={(event) => setBucket(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                  </select>
                </div>
              </div>

              {filtersError ? (
                <div className="text-sm text-rose-600">{filtersError}</div>
              ) : null}
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-sm text-slate-500">Loading report...</div>
            )}

            {!loading && report && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
                    Top Themes
                    <InfoTooltip text="Most frequent themes during the selected range." />
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                    {topThemes.length === 0 && (
                      <div className="text-sm text-slate-500">
                        No theme data for this range.
                      </div>
                    )}
                    {topThemes.map((row) => (
                      <div
                        key={row.theme}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="text-xs uppercase tracking-wide text-slate-500">
                          {row.theme.replace(/_/g, " ")}
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {row.count}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  id={HELP_ANCHORS.details}
                  className={`grid grid-cols-1 gap-6 lg:grid-cols-2 ${getSectionClassName(
                    HELP_ANCHORS.details
                  )}`}
                >
                  {renderTable(byCategory, "Category")}
                  {renderTable(
                    byStaff,
                    "Staff",
                    broadScope && includeBusinessSalonCols,
                  )}
                </div>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {renderTable(
                    byChannel,
                    "Channel",
                    broadScope && includeBusinessSalonCols,
                  )}
                  {renderTable(byDay, "Day of week")}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left w-40">Theme</th>
                        <th className="px-3 py-2 text-left">Bucket</th>
                        <th className="px-3 py-2 text-right w-24">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {trend.length === 0 && (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={3}>
                            No trend data.
                          </td>
                        </tr>
                      )}
                      {trend.map((row, idx) => (
                        <tr key={`${row.theme}-${row.bucket_label}-${idx}`}>
                          <td className="px-3 py-3 text-slate-700">{row.theme}</td>
                          <td className="px-3 py-3 text-slate-700">
                            {row.bucket_label}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-700">
                            {row.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
